import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { withRouter } from 'react-router-dom';
import { Container } from 'reactstrap';
import { useIntl } from 'react-intl';

import { fetchUserProfile } from '../actions/Auth';

import {
  APIintegrationGet,
  APIintegrations,
  APIintegrationDisconnect,
} from '../api/Integrations';

import GoBackToListButton from '../components/GoBackToListButton';
import IntegrationsHeader from '../components/Integrations/Header';
import IntegrationsList from '../components/Integrations/List';
import IntegrationsIntegration from '../components/Integrations/Integration';
import IntegrationsIntegrationTabs from '../components/Integrations/IntegrationTabs';

import { userCan } from '../helpers/Auth';
import { addFlashMessage } from '../helpers/FlashMessages';
import { getIntegrationsURLPath } from '../helpers/Integrations';

import ErrorBoundary from '../components/ErrorBoundary';
import Loading from '../components/Loading';
import NothingFound from '../components/NothingFound';

const Integrations = ({ dispatch, match }) => {
  const intl = useIntl();

  const [integrations, setIntegrations] = useState([]);
  const [integration, setIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [disconnectingServiceId, setDisconnectingServiceId] = useState(null);

  const { serviceId } = match.params;

  useEffect(() => {
    if (disconnectingServiceId !== null || serviceId) {
      return;
    }
    setLoading(true);
    APIintegrations().then((response) => {
      setIntegrations(response);
      setLoading(false);
    });
  }, [disconnectingServiceId, serviceId]);

  useEffect(() => {
    if (!serviceId) {
      setIntegration(null);
      setNotFound(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    APIintegrationGet(serviceId).then((response) => {
      if (!userCan('integrations', response)) {
        setNotFound(true);
      }
      setIntegration(response);
    }).catch(() => {
      setNotFound(true);
    }).finally(() => {
      setLoading(false);
    });
  }, [serviceId]);

  const handleDisconnect = (sId) => {
    setDisconnectingServiceId(sId);
    APIintegrationDisconnect(sId).then((response) => {
      if (response.status === 'OK') {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'You have been disconnected from the service' }),
        });
        if (integration) {
          setIntegration(prevState => ({ ...prevState, is_connected: false }));
        }
        dispatch(fetchUserProfile());
      } else {
        addFlashMessage({
          type: 'danger',
          text: intl.formatMessage({ defaultMessage: 'Something went wrong' }),
        });
      }
      setDisconnectingServiceId(null);
    });
  };

  return (
    <div className={classnames('main-content', { 'integrations-page': !serviceId, 'integration-page': serviceId })}>
      <Container fluid>
        {!(loading && serviceId) && (
          <>
            {serviceId && (
              <GoBackToListButton
                goBackUrl={getIntegrationsURLPath()}
                goBackText={intl.formatMessage({ defaultMessage: 'Back to all Integrations' })}
              />
            )}
            {!notFound && (
              <IntegrationsHeader integration={integration} />
            )}
          </>
        )}
        {loading ? (
          <Loading />
        ) : (
          <ErrorBoundary>
            {serviceId ? (
              <>
                {notFound ? (
                  <NothingFound />
                ) : (
                  <>
                    <IntegrationsIntegrationTabs />
                    <IntegrationsIntegration
                      integration={integration}
                      disconnecting={disconnectingServiceId === serviceId}
                      handleDisconnect={handleDisconnect}
                    />
                  </>
                )}
              </>
            ) : (
              <IntegrationsList
                integrations={integrations}
                disconnectingServiceId={disconnectingServiceId}
                handleDisconnect={handleDisconnect}
              />
            )}
          </ErrorBoundary>
        )}
      </Container>
    </div>
  );
};

Integrations.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
};

export default withRouter(connect()(Integrations));
